import { Suspense, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import type { UsuarioSessao } from '@crm/shared'
import { useSaude } from '../api/hooks'
import {
  IconeAnuncio,
  IconeBatePapo,
  IconeCalendario,
  IconeConfiguracao,
  IconeContatos,
  IconeEmpresa,
  IconeEstatisticas,
  IconeImagem,
  IconeLeads,
  IconePainel,
} from '../componentes/icones'
import { Marca } from '../componentes/Marca'
import { AvisoDeConexao } from '../componentes/AvisoDeConexao'
import { MenuDoUsuario } from '../componentes/MenuDoUsuario'
import { NavegacaoPrincipal } from '../componentes/NavegacaoPrincipal'
import { AcoesAgendadas } from '../componentes/AcoesAgendadas'
import { AvisosDeRecusa } from '../componentes/AvisoDeRecusa'
import { AvisoDeAcesso } from '../componentes/AvisoDeAcesso'
import { AvisoDeFimDoHorario } from '../componentes/FimDoHorario'
import { TituloComContador } from '../componentes/TituloComContador'
import { BarraDeAvisos, SinoDeNotificacoes } from '../componentes/Notificacoes'
import { PedidosDeApoio } from '../componentes/PedidosDeApoio'
import { Carregando, juntar } from '../componentes/ui'
import { useTema } from '../componentes/tema'
import { abreConfiguracaoDaAdministracao, pode } from '../permissao'
import { useSomDasMensagens } from '../tempo-real/som'

/**
 * Todas as telas da seção 5 da especificação. O menu mostra só o que a
 * permissão do usuário libera.
 */
export function Layout({ usuario }: { usuario: UsuarioSessao }) {
  const local = useLocation()
  /*
    O canto de baixo, à esquerda, é uma pilha só (10/10/2026): os pedidos de
    apoio e a contagem do fim do horário entram nela um sobre o outro. Cada um
    no seu canto fixo, os dois se cobriam; no centro ou à direita, a contagem
    cobria o rodapé das janelas e o botão de enviar.
  */
  const [canto, setCanto] = useState<HTMLDivElement | null>(null)

  // O som e o tema valem em todas as telas, e não só no bate-papo (ADR 0040).
  useSomDasMensagens(usuario.preferencias)
  useTema(usuario.preferencias.tema)

  /**
   * O ícone acompanha o rótulo, nunca o substitui: o texto continua ali para
   * quem entra hoje na equipe, e o desenho é o que dá para reconhecer de
   * relance depois da primeira semana.
   */
  const itens = [
    {
      para: '/bate-papo',
      rotulo: 'Bate-papo',
      Icone: IconeBatePapo,
      visivel: pode(usuario, 'bate_papo.ver'),
    },
    { para: '/leads', rotulo: 'Leads', Icone: IconeLeads, visivel: pode(usuario, 'leads.ver') },
    /*
      Contatos e Empresas ao lado de Leads, como no RD Station: o funil, as
      pessoas e as empresas. Mesma chave de Leads, porque é o mesmo cadastro
      de clientes visto de três lados.
    */
    {
      para: '/contatos',
      rotulo: 'Contatos',
      Icone: IconeContatos,
      visivel: pode(usuario, 'leads.ver'),
    },
    {
      para: '/empresas',
      rotulo: 'Empresas',
      Icone: IconeEmpresa,
      visivel: pode(usuario, 'leads.ver'),
    },
    {
      para: '/calendario',
      rotulo: 'Calendário',
      Icone: IconeCalendario,
      visivel: pode(usuario, 'calendario.ver'),
    },
    /*
      Estatísticas e o Painel do admin foram para a Administração (ADR 0059):
      são telas de quem acompanha a operação, e não de quem atende.
    */
    /*
      O Chatbot saiu da barra e virou aba da Configuração: é ajuste de vez em
      quando, e não tela do dia a dia.
    */
    /*
      Listas e Logs saíram daqui e viraram abas da Configuração: são telas de
      administrador, abertas de vez em quando, e ocupavam largura fixa ao lado
      das que a equipe usa o dia inteiro.
    */
    /*
      Configuração saiu daqui e foi para o menu do usuário, acima de "Sair".
      É a tela que se abre para ajustar alguma coisa e sair, não uma em que se
      trabalha — não precisa ocupar largura fixa ao lado das que se usam o dia
      inteiro.
    */
  ].filter((item) => item.visivel)

  /*
    Dois produtos na mesma casa (ADR 0057): o CRM e o FaroEscolar Marketing.
    A barra mostra as telas do produto aberto; a página de escolha (`/inicio`,
    pela logo) não mostra nenhuma.
  */
  const area = local.pathname.startsWith('/marketing')
    ? 'marketing'
    : local.pathname.startsWith('/admin')
      ? 'administracao'
      : local.pathname === '/inicio'
        ? 'inicio'
        : 'crm'
  const postsDoMarketing = pode(usuario, 'marketing.ver')
  const itensDoMarketing = [
    { para: '/marketing', rotulo: 'Calendário', Icone: IconeCalendario, exato: true, visivel: postsDoMarketing },
    { para: '/marketing/posts', rotulo: 'Posts', Icone: IconeImagem, visivel: postsDoMarketing },
    { para: '/marketing/perfis', rotulo: 'Perfis', Icone: IconeContatos, visivel: postsDoMarketing },
    // Saiu do "Mais" do CRM (ADR 0068): os leads de anúncio são assunto do Marketing.
    { para: '/marketing/anuncios', rotulo: 'Anúncios', Icone: IconeAnuncio, visivel: pode(usuario, 'anuncios.ver') },
  ].filter((item) => item.visivel)
  // FaroEscolar Administração (ADR 0059): cada tela pela permissão dela.
  const itensDaAdministracao = [
    {
      para: '/admin/painel',
      rotulo: 'Painel',
      Icone: IconePainel,
      visivel: pode(usuario, 'painel.ver'),
    },
    {
      para: '/admin/estatisticas',
      rotulo: 'Estatísticas',
      Icone: IconeEstatisticas,
      visivel: pode(usuario, 'estatisticas.ver'),
    },
    {
      para: '/admin/configuracao',
      rotulo: 'Configuração',
      Icone: IconeConfiguracao,
      visivel: abreConfiguracaoDaAdministracao(usuario),
    },
  ].filter((item) => item.visivel)

  return (
    /*
      Barra de navegação no topo, conteúdo embaixo.

      `h-screen` com `overflow-hidden`, e a rolagem só dentro do conteúdo:
      assim a barra fica sempre à vista, mesmo em página longa como a de
      estatísticas, sem precisar voltar ao topo para trocar de tela.
    */
    /*
      `h-dvh` e não `h-screen`: `100vh` ignora a barra de endereço que aparece
      e some, e sobra ou falta faixa embaixo conforme ela se mexe. A unidade
      dinâmica acompanha a altura que existe de verdade.
    */
    /*
      Na impressão a moldura sai do caminho: sem altura presa à janela e sem a
      rolagem própria do miolo, o papel recebe a página inteira, e não só o
      pedaço que cabia na tela (o defeito do "imprimir" das Estatísticas).
    */
    <div className="flex h-dvh w-full flex-col overflow-hidden print:block print:h-auto print:overflow-visible">
      {/*
        A barra do topo é escura nos dois temas, como o cabeçalho do site
        FaroEscolar (ADR 0055): carvão, com a faixa azul, turquesa e verde da marca
        embaixo. `tema-escuro` dá a ela os valores do modo escuro, e o que está
        dentro (navegação, sino, menu do usuário e os menus que abrem dali) sai
        claro sobre escuro com os tokens de sempre.
      */}
      {/*
        A faixa de número fora do ar fica acima de tudo, inclusive da barra de
        navegação: é o único aviso que vale mais que a tela aberta no momento.
      */}
      <div className="print:hidden">
        <AvisoDeConexao usuario={usuario} />
      </div>

      {/*
        No celular, folgas menores (12px na borda, 8px entre os grupos): com as de computador, sobravam
        90px para a navegação entre a marca e o sino, e o "Mais" passava por cima do sino.
      */}
      <header className="tema-escuro relative z-20 flex shrink-0 items-center gap-2 bg-tinta-100 px-3 py-2.5 text-tinta-900 sm:gap-4 sm:px-5 shadow-media print:hidden after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:bg-linear-to-r after:from-marca after:via-faro-turquesa after:to-faro-verde">
        {/*
          A marca encolhe antes da navegação.

          Medido: a fila de telas pedia 707px e tinha 693 — rolava de lado, e a
          barra de rolagem passava por cima de "Logs" e do sino. A marca sozinha
          ocupava 149px para dizer o nome de um sistema que a pessoa abre o dia
          inteiro; menor, continua reconhecível e devolve o espaço a quem
          precisa dele.
        */}
        {/* A logo leva à escolha entre CRM e Marketing (ADR 0057). */}
        <Link
          to="/inicio"
          title="Escolher entre CRM e Marketing"
          className="shrink-0 rounded-xl outline-none transition hover:opacity-85 focus-visible:ring-2 focus-visible:ring-marca/60"
        >
          <Marca
            produto={
              area === 'marketing' ? 'Marketing' : area === 'administracao' ? 'Administração' : 'CRM'
            }
            comSelo={area === 'marketing' || area === 'administracao'}
          />
        </Link>

        {/*
          A navegação mantém o texto ao lado do ícone: ícone sozinho vira
          adivinhação para quem entrou esta semana. Em janela estreita, as telas
          que não cabem passam para o "Mais" em vez de a fila rolar de lado.
        */}
        {area === 'inicio' ? (
          <div className="flex-1" />
        ) : area === 'marketing' ? (
          <NavegacaoPrincipal itens={itensDoMarketing} />
        ) : area === 'administracao' ? (
          <NavegacaoPrincipal itens={itensDaAdministracao} />
        ) : (
          <NavegacaoPrincipal itens={itens} />
        )}

        {/* Sino e usuário juntos à direita: o que é da pessoa, não da tela. */}
        <div className="flex shrink-0 items-center gap-1">
          <SinoDeNotificacoes />
          <MenuDoUsuario usuario={usuario} />
        </div>
      </header>

      {/*
        O bate-papo vai de borda a borda; as outras telas são documentos e
        pedem respiro em volta.

        O chat tinha `p-2`, oito pixels de moldura em volta de uma tela que
        disputa cada pixel — e que, com a barra fixa no topo, já está delimitada
        por cima. Agora encosta nas quatro bordas.
      */}
      {/*
        `relative`: a área que rola é a referência de posição de tudo que fica
        dentro dela. Sem isso, elemento com `position: absolute` e sem outro
        pai posicionado (o texto escondido para leitor de tela, `sr-only`, dos
        números das Estatísticas) se ancorava na página inteira, esticava o
        documento para baixo e criava uma segunda rolagem, cheia de vazio.
      */}
      <main
        className={juntar(
          'relative min-h-0 flex-1 overflow-y-auto print:overflow-visible print:p-0',
          // 16px no celular: 24px de cada lado tiravam 13% de uma tela de 375px.
          local.pathname.startsWith('/bate-papo') ? '' : 'p-4 sm:p-6',
        )}
      >
        <div className="print:hidden">
          <AvisoDoWorker />
        </div>
        {/* A página sob demanda (ADR 0067) carrega aqui dentro: o menu fica parado. */}
        <Suspense fallback={<Carregando texto="Abrindo a tela…" />}>
          <Outlet />
        </Suspense>
      </main>

      {/*
        Avisos na borda direita (ADR 0035): as três marcas com contador, e a
        caixa de cada uma abrindo no clique ou quando chega aviso novo.
      */}
      {/* Nada do que flutua na tela vai para o papel. */}
      <div className="print:hidden">
        <BarraDeAvisos />

        {/* Ações em massa na contagem dos 15 segundos, com o "Desfazer". */}
        <AcoesAgendadas />

        {/* Ação recusada que a tela não mostrou (ADR 0074). */}
        <AvisosDeRecusa />

        <div
          ref={setCanto}
          className="pointer-events-none fixed bottom-4 left-4 z-[55] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 print:hidden"
        />

        {/* Pedido de apoio para aceitar ou recusar, e o aviso da resposta (ADR 0043). */}
        <PedidosDeApoio usuario={usuario} canto={canto} />

        {/* Acesso mudou com a tela aberta: trava até recarregar (mapa de uso, 08/10/2026). */}
        <AvisoDeAcesso />

        {/* Fim do horário de trabalho: a contagem e o "Mais 30 minutos" (10/10/2026). */}
        <AvisoDeFimDoHorario usuario={usuario} canto={canto} />

        {/* "(3) CRM FaroEscolar" na aba do navegador (mapa de uso, 08/10/2026). */}
        {pode(usuario, 'bate_papo.ver') ? <TituloComContador /> : null}
      </div>
    </div>
  )
}

/**
 * Faixa de alerta quando o worker está fora.
 *
 * Aparece porque a falta dele é invisível: a tela continua igual, as conversas
 * continuam chegando, e só o que roda em segundo plano para — download de
 * mídia, cronômetro, encerramento por inatividade, lembrete, disparo
 * agendado. O sintoma que apareceu na prática foi "o chat não baixa nenhum
 * arquivo", e a causa levou um tempo para ser encontrada.
 */
function AvisoDoWorker() {
  const { data } = useSaude()
  if (!data || data.worker.ativo || !data.worker.aviso) return null

  return (
    <div className="mb-5 rounded-xl border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-suave">
      <p className="font-semibold">Os processos de segundo plano estão parados.</p>
      <p className="mt-0.5 text-amber-800">{data.worker.aviso}</p>
    </div>
  )
}
